/**
 * Contexto de Sidebar
 *
 * Gerencia o estado global da sidebar (colapsada/expandida)
 * e persiste a preferência do usuário no localStorage.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';

/**
 * Interface do contexto de sidebar
 */
interface SidebarContextData {
  /** Se a sidebar está colapsada */
  isCollapsed: boolean;
  /** Se o menu mobile está aberto */
  isMobileOpen: boolean;
  /** Alterna o estado colapsado */
  toggleCollapsed: () => void;
  /** Define o estado colapsado */
  setCollapsed: (collapsed: boolean) => void;
  /** Abre o menu mobile */
  openMobile: () => void;
  /** Fecha o menu mobile */
  closeMobile: () => void;
  /** Alterna o menu mobile */
  toggleMobile: () => void;
}

/**
 * Contexto da sidebar
 */
const SidebarContext = createContext<SidebarContextData | undefined>(undefined);

/**
 * Chave do localStorage para persistir preferência
 */
const STORAGE_KEY = '@vendeai:sidebar-collapsed';

/**
 * Props do provider
 */
interface SidebarProviderProps {
  children: ReactNode;
}

/**
 * Provider do contexto de sidebar
 *
 * Gerencia o estado da sidebar e persiste a preferência
 * do usuário no localStorage.
 */
export function SidebarProvider({ children }: SidebarProviderProps) {
  // Estado de colapso da sidebar (desktop)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Tenta recuperar preferência do localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : false;
  });

  // Estado do menu mobile
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  /**
   * Efeito para persistir preferência no localStorage
   */
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  /**
   * Efeito para fechar menu mobile ao redimensionar para desktop
   */
  useEffect(() => {
    const handleResize = () => {
      // Fecha menu mobile se tela for maior que md (768px)
      if (window.innerWidth >= 768) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Efeito para bloquear scroll do body quando menu mobile está aberto
   */
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  /**
   * Alterna o estado colapsado da sidebar
   */
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev: boolean) => !prev);
  }, []);

  /**
   * Define o estado colapsado da sidebar
   */
  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
  }, []);

  /**
   * Abre o menu mobile
   */
  const openMobile = useCallback(() => {
    setIsMobileOpen(true);
  }, []);

  /**
   * Fecha o menu mobile
   */
  const closeMobile = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  /**
   * Alterna o menu mobile
   */
  const toggleMobile = useCallback(() => {
    setIsMobileOpen((prev: boolean) => !prev);
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        isCollapsed,
        isMobileOpen,
        toggleCollapsed,
        setCollapsed,
        openMobile,
        closeMobile,
        toggleMobile,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

/**
 * Hook para usar o contexto de sidebar
 *
 * @returns Dados e funções do contexto de sidebar
 * @throws Error se usado fora do SidebarProvider
 *
 * @example
 * const { isCollapsed, toggleCollapsed } = useSidebar();
 */
export function useSidebar(): SidebarContextData {
  const context = useContext(SidebarContext);

  if (!context) {
    throw new Error('useSidebar deve ser usado dentro de um SidebarProvider');
  }

  return context;
}
